// frontend/src/pages/PrivacyPolicy.jsx
// This component renders a minimal Privacy Policy page with increased top spacing
import { useEffect } from "react";
import HeaderSimple from "../components/pages/home/HeaderSimple";
import Footer from "../components/pages/home/Footer";
import { updatePageTitle } from "../utils/titleUtils";

// This is the main privacy policy page component
const PrivacyPolicy = () => {
  // This sets the browser tab title when the page loads
  useEffect(() => {
    updatePageTitle("Privacy Policy");
    // This ensures the page scrolls to the top when navigated to
    window.scrollTo(0, 0);
  }, []);

  // This returns minimal layout without containers, borders or shadows
  return (
    <div className="min-h-screen bg-bg-60">
      {/* This shows the simple header at the top */}
      <HeaderSimple />

      {/* This is the main content with more top margin and simple typography */}
      <main className="pt-48 pb-20 px-6">
        {/* This wraps the content to a max width similar to the home page */}
        <div className="w-[90%] max-w-5xl mx-auto">
          {/* This is the page title and effective date centered only */}
          <div className="text-center mb-10">
            <h1 className="text-4xl font-bold text-fg-50 mb-2">
              Privacy Policy
            </h1>
            <p className="text-fg-60">Effective date: November 15, 2025</p>
          </div>

          {/* This section explains what data we collect */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-fg-50 mb-3">
              Information We Collect
            </h2>
            <p className="text-fg-60">
              We collect account information you provide (such as name and
              email), usage and device information, and content you submit in
              chats. Conversation metadata (like timestamps, thread identifiers
              and model selection) may be stored to enable features such as
              history, search, and analytics.
            </p>
          </section>

          {/* This section explains how data is used */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-fg-50 mb-3">
              How We Use Your Data
            </h2>
            <p className="text-fg-60">
              We use your data to provide and improve the service, personalize
              your experience, enforce acceptable use, prevent abuse, and comply
              with legal obligations. We also use minimal identifiers to
              authorize requests and to maintain your session.
            </p>
          </section>

          {/* This section explains data sharing with LLM providers */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-fg-50 mb-3">
              LLM Providers and Data Sharing
            </h2>
            <p className="text-fg-60">
              When you use our multi‑model chat features, your prompts and
              relevant context may be sent to the third‑party large language
              model providers you select (for example, provider APIs you opt to
              use). These providers process the data to return responses. We do
              not sell your data. We do not train our own models using your chat
              content. Third‑party providers may use data as described in their
              policies; we recommend reviewing each provider’s privacy terms
              before use.
            </p>
          </section>

          {/* This section explains storage, retention and deletion */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-fg-50 mb-3">
              Storage and Retention
            </h2>
            <p className="text-fg-60">
              We store account data and chat history to deliver features like
              conversation continuity and organization management. Retention
              periods vary based on feature requirements and legal obligations.
              You may request deletion of your account and associated data,
              subject to limits where we must retain information for security,
              billing, or compliance.
            </p>
          </section>

          {/* This section explains security practices */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-fg-50 mb-3">Security</h2>
            <p className="text-fg-60">
              We use industry‑standard measures to protect your information,
              including transport encryption, access controls, and regular
              monitoring. Authentication is handled via secure tokens and
              trusted identity providers. No method of transmission or storage
              is fully risk‑free; please avoid sharing sensitive personal
              information in prompts.
            </p>
          </section>

          {/* This section explains cookies and similar technologies */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-fg-50 mb-3">
              Cookies and Preferences
            </h2>
            <p className="text-fg-60">
              We use cookies and similar technologies to remember your settings,
              keep you signed in, and measure usage. You can control cookies
              through your browser. Disabling some cookies may impact
              functionality.
              <br />
              We use Google Analytics to understand how visitors use our website
              and to improve our services. Google Analytics collects information
              such as pages visited, time spent on pages, and device/browser
              data. This information is processed in aggregate and used for site
              performance and product improvements. For more information about
              how Google collects and uses your data, visit the{" "}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Privacy Policy
              </a>
              .
              <br />
              <br />
              We also partner with Microsoft Clarity to capture how you use and
              interact with our website through behavioral metrics, heatmaps,
              and session replay to improve and market our products/services.
              Website usage data is captured using first- and third-party
              cookies and other tracking technologies. We use this information
              for site optimization, fraud/security purposes, and advertising.
              For more information about how Microsoft collects and uses your
              data, visit the{" "}
              <a
                href="https://privacy.microsoft.com/en-us/privacystatement"
                target="_blank"
                rel="noopener noreferrer"
              >
                Microsoft Privacy Statement
              </a>
              .
            </p>
          </section>

          {/* This section explains user rights and controls */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-fg-50 mb-3">
              Your Rights
            </h2>
            <p className="text-fg-60">
              Depending on your location, you may have rights to access,
              correct, delete, or export your data. You may also object to or
              restrict certain processing. To exercise these rights, contact us
              using the details below.
            </p>
          </section>

          {/* This section explains international transfers and children policy */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-fg-50 mb-3">
              International Transfers and Children
            </h2>
            <p className="text-fg-60">
              Data may be processed in regions where our infrastructure or
              third‑party providers operate and be subject to local laws. Our
              service is not directed to children under 13 and we do not
              knowingly collect their information.
            </p>
          </section>

          {/* This section explains changes and contact information */}
          <section>
            <h2 className="text-2xl font-semibold text-fg-50 mb-3">
              Changes and Contact
            </h2>
            <p className="text-fg-60">
              We may update this policy to reflect changes in our practices. If
              we make material changes, we will notify you through the service
              or by other appropriate means. If you have questions, contact us
              at
              <span className="font-medium"> info@leebai.com</span>.
            </p>
          </section>
        </div>
      </main>

      {/* This shows the global footer at the bottom */}
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
